import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Patient, AIReview, AIReviewAnalysis, AIReviewRequest } from "common/models/types";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Spinner from "components/atoms/Spinner";
import { COLOR_SYSTEM } from "components/design/design-system";
import ReactMarkdown from "react-markdown";
import {
  analyzePrescriptions,
  fetchAnalysisResult,
  fetchAnalysisStatus,
  fetchPatientAnalysisHistory,
  fetchAnalysisStream,
} from "../../../api/aiReviewService";
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import { WaitingManager } from "components/organisms/CommonUI/WaitingPopup/WaitingManager";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";

interface AnalysisTabProps {
  patient: Patient;
  onReloadPatient?: (patientId: number) => void;
}

// 추가: 세부 정보 뷰 타입 정의
type DetailViewType = "request" | "patientSnap" | "analysis" | "actualPrompt" | null;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const AnalysisItem = styled.div<{ isSelected?: boolean }>`
  padding: 16px;
  border-radius: 8px;
  background-color: ${(props) =>
    props.isSelected ? COLOR_SYSTEM.get("Blue")?.[50] : COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid
    ${(props) =>
      props.isSelected ? COLOR_SYSTEM.get("Blue")?.[300] : COLOR_SYSTEM.get("Gray")?.[200]};
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.isSelected ? COLOR_SYSTEM.get("Blue")?.[50] : COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const AnalysisList = styled.div`
  margin-top: 16px;
  max-height: 400px;
  overflow-y: auto;
`;

const AnalysisResult = styled.div`
  background-color: white;
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

// 마크다운 스타일 추가
const MarkdownContent = styled.div`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    line-height: 1.25;
  }

  h1 {
    font-size: 2em;
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
    padding-bottom: 0.3em;
  }

  h2 {
    font-size: 1.5em;
    border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[200]};
    padding-bottom: 0.3em;
  }

  h3 {
    font-size: 1.25em;
  }

  h4 {
    font-size: 1em;
  }

  p {
    margin-top: 0;
    margin-bottom: 16px;
  }

  ul,
  ol {
    padding-left: 2em;
    margin-top: 0;
    margin-bottom: 16px;
  }

  li {
    margin-top: 0.25em;
  }

  blockquote {
    margin: 0;
    padding: 0 1em;
    color: ${COLOR_SYSTEM.get("Gray")?.[600]};
    border-left: 0.25em solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  }

  code {
    padding: 0.2em 0.4em;
    margin: 0;
    font-size: 85%;
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
    border-radius: 3px;
  }

  pre {
    padding: 16px;
    overflow: auto;
    font-size: 85%;
    line-height: 1.45;
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
    border-radius: 3px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 16px;
  }

  table th,
  table td {
    padding: 6px 13px;
    border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  }

  table tr {
    background-color: #fff;
    border-top: 1px solid ${COLOR_SYSTEM.get("Gray")?.[300]};
  }

  table tr:nth-child(2n) {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

const DetailButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const StreamContainer = styled.div`
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border: 1px solid ${COLOR_SYSTEM.get("Blue")?.[200]};
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  margin-bottom: 24px;
`;

const StreamContent = styled.div`
  background-color: white;
  border-radius: 4px;
  padding: 12px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  min-height: 100px;
  max-height: 300px;
  overflow-y: auto;
  font-family: "Courier New", monospace;
  font-size: 14px;
`;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
  background-color: ${(props) => {
    switch (props.status) {
      case "pending":
        return COLOR_SYSTEM.get("Gray")?.[200];
      case "processing":
        return COLOR_SYSTEM.get("Blue")?.[100];
      case "completed":
        return COLOR_SYSTEM.get("Green")?.[100];
      case "failed":
        return COLOR_SYSTEM.get("Red")?.[100];
      case "streaming":
        return COLOR_SYSTEM.get("Purple")?.[100];
      default:
        return COLOR_SYSTEM.get("Gray")?.[200];
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return COLOR_SYSTEM.get("Gray")?.[700];
      case "processing":
        return COLOR_SYSTEM.get("Blue")?.[700];
      case "completed":
        return COLOR_SYSTEM.get("Green")?.[700];
      case "failed":
        return COLOR_SYSTEM.get("Red")?.[700];
      case "streaming":
        return COLOR_SYSTEM.get("Purple")?.[700];
      default:
        return COLOR_SYSTEM.get("Gray")?.[700];
    }
  }};
`;

const StreamActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
`;

const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")} ${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
};

const AnalysisTab: React.FC<AnalysisTabProps> = ({ patient, onReloadPatient }) => {
  const [analysisHistory, setAnalysisHistory] = useState<AIReview[]>([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AIReview | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null);
  const [analysisStatus, setAnalysisStatus] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [detailViewType, setDetailViewType] = useState<DetailViewType>(null);

  // 스트리밍 관련 상태
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [streamContent, setStreamContent] = useState<string>("");
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const streamContainerRef = useRef<HTMLDivElement>(null);

  // 자동 스크롤 함수
  const scrollToBottom = () => {
    if (streamContainerRef.current) {
      streamContainerRef.current.scrollTop = streamContainerRef.current.scrollHeight;
    }
  };

  // 환자의 분석 이력 로드
  const loadAnalysisHistory = useCallback(async () => {
    if (!patient || !patient.id) return;

    setIsLoading(true);
    try {
      const response = await fetchPatientAnalysisHistory(patient.id);
      if (response.error) {
        throw new Error(response.message || "분석 이력을 불러오는데 실패했습니다.");
      }
      setAnalysisHistory(response.data || []);
    } catch (error) {
      log(LogLevel.ERROR, "분석 이력 로드 오류:", error);
      ToastManager.show({
        content: "분석 이력을 불러오는데 실패했습니다.",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [patient]);

  // 컴포넌트 마운트 시 분석 이력 로드
  useEffect(() => {
    loadAnalysisHistory();
  }, [loadAnalysisHistory]);

  // 분석 상태 폴링
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (
      currentAnalysisId &&
      (analysisStatus === "pending" || analysisStatus === "processing") &&
      !isStreaming
    ) {
      intervalId = setInterval(async () => {
        try {
          const response = await fetchAnalysisStatus(currentAnalysisId);
          if (response.error) {
            throw new Error(response.message || "분석 상태 조회에 실패했습니다.");
          }

          const status = response.data?.status;
          setAnalysisStatus(status || null);
          setAnalysisProgress(response.data?.progress || 0);

          if (status === "completed" || status === "failed") {
            clearInterval(intervalId);
            setIsAnalyzing(false);

            if (status === "completed") {
              await loadAnalysisResult(currentAnalysisId);
              await loadAnalysisHistory();
            } else {
              ToastManager.show({
                content: "분석에 실패했습니다.",
                duration: 3000,
              });
            }
          }
        } catch (error) {
          log(LogLevel.ERROR, "분석 상태 조회 오류:", error);
          clearInterval(intervalId);
          setIsAnalyzing(false);
        }
      }, 3000); // 3초마다 상태 확인
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentAnalysisId, analysisStatus, loadAnalysisHistory, isStreaming]);

  // 컴포넌트 언마운트 시 스트림 연결 정리
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
        streamCleanupRef.current = null;
      }
    };
  }, []);

  // 스트림 데이터가 업데이트될 때마다 스크롤
  useEffect(() => {
    if (isStreaming) {
      scrollToBottom();
    }
  }, [streamContent, isStreaming]);

  // 분석 결과 로드
  const loadAnalysisResult = async (analysisId: string) => {
    try {
      const response = await fetchAnalysisResult(analysisId);
      if (response.error) {
        throw new Error(response.message || "분석 결과를 불러오는데 실패했습니다.");
      }

      setSelectedAnalysis(response.data || null);

      ToastManager.show({
        content: "분석이 완료되었습니다.",
        duration: 3000,
      });
    } catch (error) {
      log(LogLevel.ERROR, "분석 결과 로드 오류:", error);
      ToastManager.show({
        content: "분석 결과를 불러오는데 실패했습니다.",
        duration: 3000,
      });
    }
  };

  // 처방전 분석 스트림 시작
  // const startAnalysisStream = (analysisId: string) => {
  //   // 이미 스트리밍 중이면 중지
  //   if (streamCleanupRef.current) {
  //     streamCleanupRef.current();
  //   }

  //   setIsStreaming(true);
  //   setStreamContent("");
  //   setAnalysisStatus("streaming");

  //   // 스트림 연결 설정
  //   const cleanup = fetchAnalysisStream(
  //     analysisId,
  //     (data) => {
  //       // 스트림 데이터 누적
  //       setStreamContent((prev) => prev + data);
  //     },
  //     (error) => {
  //       log(LogLevel.ERROR, "스트림 오류:", error);
  //       ToastManager.show({
  //         content: "실시간 분석 스트림에 문제가 발생했습니다.",
  //         duration: 3000,
  //       });
  //       setIsStreaming(false);
  //       streamCleanupRef.current = null;
  //     },
  //     () => {
  //       // 스트림 완료
  //       ToastManager.show({
  //         content: "실시간 분석이 완료되었습니다.",
  //         duration: 3000,
  //       });
  //       setIsStreaming(false);
  //       setAnalysisStatus("completed");
  //       loadAnalysisHistory();
  //       streamCleanupRef.current = null;
  //     }
  //   );

  //   streamCleanupRef.current = cleanup;
  // };

  // 스트림 중지
  const stopAnalysisStream = () => {
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;

      ToastManager.show({
        content: "실시간 분석 스트림을 중지했습니다.",
        duration: 3000,
      });

      setIsStreaming(false);
    }
  };

  // 분석 아이템 선택
  const handleSelectAnalysis = async (analysis: AIReview) => {
    if (selectedAnalysis && selectedAnalysis.id === analysis.id) {
      setSelectedAnalysis(null);
      return;
    }

    setSelectedAnalysis(analysis);
    setDetailViewType(null); // 상세 보기 초기화
  };

  // 분석 이력 리로드
  const refreshAnalysisHistory = () => {
    loadAnalysisHistory();
    setSelectedAnalysis(null);
    setDetailViewType(null);
  };

  // 저장된 분석 결과 스트리밍으로 보기
  const viewAnalysisInStreamMode = (analysisId: string) => {
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
    }

    // startAnalysisStream(analysisId);
  };

  // 상세 정보 보기 함수
  const showDetailInfo = (type: DetailViewType) => {
    if (!selectedAnalysis) return;

    setDetailViewType(type);

    let title = "";
    let content = null;

    switch (type) {
      case "request":
        title = "AI 리뷰 요청 옵션";
        content = selectedAnalysis.request
          ? JSON.stringify(selectedAnalysis.request, null, 2)
          : "데이터 없음";
        break;
      case "patientSnap":
        title = "환자 정보 스냅샷";
        content = selectedAnalysis.patientSnap
          ? JSON.stringify(selectedAnalysis.patientSnap, null, 2)
          : "데이터 없음";
        break;
      case "analysis":
        title = "성능 분석 정보";
        content = selectedAnalysis.analysis
          ? JSON.stringify(selectedAnalysis.analysis, null, 2)
          : "데이터 없음";
        break;
      case "actualPrompt":
        title = "사용된 프롬프트";
        content = selectedAnalysis.actualPrompt || "데이터 없음";
        break;
      default:
        return;
    }

    SlideManager.show({
      body: (
        <div style={{ padding: "20px" }}>
          <Text textType="H3" style={{ marginBottom: "16px" }}>
            {title}
          </Text>
          <pre
            style={{
              backgroundColor: COLOR_SYSTEM.get("Gray")?.[50],
              padding: "16px",
              borderRadius: "8px",
              overflowX: "auto",
              fontSize: "14px",
            }}
          >
            {content}
          </pre>
        </div>
      ),
      direction: "right",
      width: "600px",
      needCloseButton: true,
    });
  };

  return (
    <Container>
      <Section>
        <Text textType="H3" style={{ marginBottom: "16px" }}>
          처방전 분석
        </Text>

        <ButtonContainer>
          <Button
            buttonType="button"
            color="Secondary"
            size="Medium"
            type="IconWithText"
            variant="Outlined"
            icon="refresh"
            right
            onClick={refreshAnalysisHistory}
            disabled={isLoading}
          >
            분석 이력 새로고침
          </Button>
        </ButtonContainer>
        {/* 
        {isStreaming && (
          <StreamContainer>
            <Text textType="Body1" style={{ marginBottom: "8px" }}>
              실시간 분석 결과
              <StatusBadge status="streaming">스트리밍</StatusBadge>
            </Text>
            <StreamContent ref={streamContainerRef}>
              {streamContent || "분석 결과를 불러오는 중입니다..."}
            </StreamContent>
            <StreamActions>
              <Button
                buttonType="button"
                color="Secondary"
                size="Small"
                type="Text"
                variant="Outlined"
                onClick={stopAnalysisStream}
              >
                스트림 중지
              </Button>
            </StreamActions>
          </StreamContainer>
        )} */}

        {isAnalyzing && !isStreaming && (
          <Section>
            <Text textType="Body1" style={{ marginBottom: "8px" }}>
              분석 진행 중...
              <StatusBadge status={analysisStatus || "pending"}>
                {analysisStatus === "pending"
                  ? "대기 중"
                  : analysisStatus === "processing"
                  ? `처리 중 ${analysisProgress > 0 ? `(${analysisProgress}%)` : ""}`
                  : analysisStatus === "completed"
                  ? "완료됨"
                  : analysisStatus === "failed"
                  ? "실패"
                  : "알 수 없음"}
              </StatusBadge>
            </Text>
            <SpinnerWrapper>
              <Spinner size="Medium" />
            </SpinnerWrapper>
          </Section>
        )}

        <Section>
          <Text textType="H4" style={{ marginBottom: "8px" }}>
            분석 이력
          </Text>

          {isLoading ? (
            <SpinnerWrapper>
              <Spinner size="Medium" />
            </SpinnerWrapper>
          ) : analysisHistory.length > 0 ? (
            <AnalysisList>
              {analysisHistory.map((analysis) => (
                <AnalysisItem
                  key={analysis.id}
                  onClick={() => handleSelectAnalysis(analysis)}
                  isSelected={selectedAnalysis?.id === analysis.id}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Text textType="Body1Bold">
                      분석 #{analysis.id.toString().substring(0, 8)}...
                      <StatusBadge status="completed">완료됨</StatusBadge>
                    </Text>
                    <Button
                      buttonType="button"
                      color="Tertiary"
                      size="Small"
                      type="Icon"
                      variant="Ghost"
                      icon="trending_flat"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewAnalysisInStreamMode(analysis.id.toString());
                      }}
                      style={{ marginLeft: "8px" }}
                    />
                  </div>
                  <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
                    {formatDate(analysis.reviewDate)}
                  </Text>
                  <Text
                    textType="Caption"
                    color={COLOR_SYSTEM.get("Gray")?.[500]}
                    style={{ marginTop: "8px", paddingLeft: "4px" }}
                  >
                    &nbsp;&nbsp;분석 시간:{" "}
                    {(
                      (analysis.analysis?.timings?.TOTAL_PROCESSING?.durationMs || 0) / 1000
                    ).toFixed(1)}
                    초&nbsp;&nbsp;|&nbsp;&nbsp;토큰:{" "}
                    {(analysis.analysis?.inputTokens || 0) + (analysis.analysis?.outputTokens || 0)}
                    개
                  </Text>
                </AnalysisItem>
              ))}
            </AnalysisList>
          ) : (
            <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")?.[600]}>
              분석 이력이 없습니다. 새로운 분석을 시작해보세요.
            </Text>
          )}
        </Section>

        {selectedAnalysis && !isStreaming && (
          <Section>
            <Text textType="H4" style={{ marginBottom: "8px" }}>
              분석 결과
            </Text>

            <DetailButtonsContainer>
              <Button
                buttonType="button"
                color="Secondary"
                size="Small"
                type="Text"
                variant="Outlined"
                onClick={() => showDetailInfo("request")}
              >
                요청 옵션 보기
              </Button>
              <Button
                buttonType="button"
                color="Secondary"
                size="Small"
                type="Text"
                variant="Outlined"
                onClick={() => showDetailInfo("patientSnap")}
              >
                환자 스냅샷 보기
              </Button>
              <Button
                buttonType="button"
                color="Secondary"
                size="Small"
                type="Text"
                variant="Outlined"
                onClick={() => showDetailInfo("analysis")}
              >
                성능 분석 보기
              </Button>
              <Button
                buttonType="button"
                color="Secondary"
                size="Small"
                type="Text"
                variant="Outlined"
                onClick={() => showDetailInfo("actualPrompt")}
              >
                프롬프트 보기
              </Button>
            </DetailButtonsContainer>

            <AnalysisResult>
              <MarkdownContent>
                <ReactMarkdown>{selectedAnalysis.reviewResult}</ReactMarkdown>
              </MarkdownContent>
            </AnalysisResult>
          </Section>
        )}
      </Section>
    </Container>
  );
};

export default AnalysisTab;
